import type { Theme } from '../types/editor'

export type ThemePalette = {
  primary: string
  secondary: string
  accent: string
  background: string
  error: string
  success: string
  warning: string
  info: string
  surface: string
  'surface-variant': string
  'editor-background': string
}

export type ThemeEffectCssVars = {
  '--escribonauta-fx-page': string
  '--escribonauta-fx-surface': string
  '--escribonauta-fx-surface-variant': string
  '--escribonauta-fx-editor': string
  '--escribonauta-fx-header': string
  '--escribonauta-fx-title': string
}

export type ThemeDefinition = {
  label: string
  dark: boolean
  colors: ThemePalette
  isPremium?: boolean
  previewBackground?: string
  swatchGradients?: Partial<Record<keyof ThemePalette, string>>
  effectCssVars?: ThemeEffectCssVars
}

export const RAINBOW_GRADIENTS = {
  page:
    'linear-gradient(160deg, #ffcdd2 0%, #ffe0b2 14%, #fff59d 30%, #c8e6c9 48%, #90caf9 66%, #ce93d8 84%, #f8bbd0 100%)',
  surface:
    'linear-gradient(135deg, #ffffff 0%, #fff176 22%, #80deea 48%, #b39ddb 74%, #ffab91 100%)',
  surfaceVariant:
    'linear-gradient(90deg, #ffcdd2 0%, #ffe0b2 18%, #fff59d 36%, #c8e6c9 54%, #90caf9 72%, #ce93d8 90%)',
  editor:
    'linear-gradient(180deg, #ffe0b2 0%, #fff9c4 14%, #c8e6c9 32%, #bbdefb 52%, #e1bee7 72%, #ffccbc 88%, #fff9c4 100%)',
  header:
    'linear-gradient(90deg, #E40303 0%, #FF8C00 17%, #FFED00 33%, #008026 50%, #24408E 67%, #732982 100%)',
  title:
    'linear-gradient(90deg, #C62828 0%, #EF6C00 20%, #F9A825 40%, #2E7D32 60%, #1565C0 80%, #6A1B9A 100%)',
  preview:
    'linear-gradient(135deg, #E40303 0%, #FF8C00 17%, #FFED00 33%, #008026 50%, #24408E 67%, #732982 83%)',
  primary: 'linear-gradient(90deg, #E40303, #FF8C00, #FFED00, #008026, #24408E, #732982)',
  secondary: 'linear-gradient(90deg, #FF8C00, #FFED00, #008026)',
  accent: 'linear-gradient(90deg, #008026, #24408E, #732982)',
} as const

export const RAINBOW_CSS_VARS: Record<string, string> = {
  '--escribonauta-rainbow-page': RAINBOW_GRADIENTS.page,
  '--escribonauta-rainbow-surface': RAINBOW_GRADIENTS.surface,
  '--escribonauta-rainbow-surface-variant': RAINBOW_GRADIENTS.surfaceVariant,
  '--escribonauta-rainbow-editor': RAINBOW_GRADIENTS.editor,
  '--escribonauta-rainbow-header': RAINBOW_GRADIENTS.header,
  '--escribonauta-rainbow-title': RAINBOW_GRADIENTS.title,
}

const AURORA_GRADIENTS = {
  page: 'linear-gradient(165deg, #061018 0%, #0b2a32 28%, #123d4a 52%, #1a2548 76%, #0d1528 100%)',
  surface: 'linear-gradient(145deg, #0e2a34 0%, #163a48 45%, #1c2444 100%)',
  surfaceVariant: 'linear-gradient(90deg, #134e4a 0%, #164e63 40%, #2e1065 100%)',
  editor: 'linear-gradient(180deg, #0c1c24 0%, #102830 40%, #152038 100%)',
  header: 'linear-gradient(90deg, #14b8a6 0%, #38bdf8 45%, #a78bfa 100%)',
  title: 'linear-gradient(90deg, #5eead4 0%, #7dd3fc 50%, #c4b5fd 100%)',
  preview: 'linear-gradient(135deg, #0b2a32 0%, #14b8a6 35%, #38bdf8 65%, #a78bfa 100%)',
  primary: 'linear-gradient(90deg, #14b8a6, #38bdf8, #a78bfa)',
  secondary: 'linear-gradient(90deg, #fbbf24, #f59e0b)',
  accent: 'linear-gradient(90deg, #38bdf8, #a78bfa)',
}

const EMBERS_GRADIENTS = {
  page: 'linear-gradient(160deg, #140a08 0%, #2a100c 30%, #3b150e 55%, #1a0c0a 100%)',
  surface: 'linear-gradient(145deg, #24120e 0%, #381810 50%, #1c0e0c 100%)',
  surfaceVariant: 'linear-gradient(90deg, #78350f 0%, #7f1d1d 55%, #450a0a 100%)',
  editor: 'linear-gradient(180deg, #1c0f0c 0%, #271410 45%, #1a0e0c 100%)',
  header: 'linear-gradient(90deg, #7f1d1d 0%, #ea580c 50%, #fbbf24 100%)',
  title: 'linear-gradient(90deg, #fca5a5 0%, #fb923c 45%, #fcd34d 100%)',
  preview: 'linear-gradient(135deg, #1c0f0c 0%, #9a3412 40%, #ea580c 70%, #fbbf24 100%)',
  primary: 'linear-gradient(90deg, #ef4444, #f97316, #fbbf24)',
  secondary: 'linear-gradient(90deg, #fbbf24, #f59e0b)',
  accent: 'linear-gradient(90deg, #fb7185, #ef4444)',
}

const NEBULA_GRADIENTS = {
  page: 'linear-gradient(165deg, #09061a 0%, #1a0b3a 32%, #2d1258 58%, #12082c 100%)',
  surface: 'linear-gradient(145deg, #180c30 0%, #2a124e 48%, #120a28 100%)',
  surfaceVariant: 'linear-gradient(90deg, #701a75 0%, #312e81 50%, #0c4a6e 100%)',
  editor: 'linear-gradient(180deg, #12082a 0%, #1c0f3c 42%, #160a30 100%)',
  header: 'linear-gradient(90deg, #7c3aed 0%, #db2777 50%, #38bdf8 100%)',
  title: 'linear-gradient(90deg, #e9d5ff 0%, #f9a8d4 45%, #7dd3fc 100%)',
  preview: 'linear-gradient(135deg, #12082a 0%, #7c3aed 35%, #db2777 65%, #38bdf8 100%)',
  primary: 'linear-gradient(90deg, #a78bfa, #f472b6, #38bdf8)',
  secondary: 'linear-gradient(90deg, #f9a8d4, #e879f9)',
  accent: 'linear-gradient(90deg, #818cf8, #38bdf8)',
}

const PARCHMENT_GRADIENTS = {
  page: 'linear-gradient(160deg, #f6ead2 0%, #efe0c0 35%, #e8d4ad 70%, #f3e6c8 100%)',
  surface: 'linear-gradient(145deg, #fff8ea 0%, #f7ebcf 50%, #f3e4c4 100%)',
  surfaceVariant: 'linear-gradient(90deg, #e8d5a8 0%, #dcc48a 50%, #d0b278 100%)',
  editor: 'linear-gradient(180deg, #fffaf0 0%, #f8edd8 50%, #f4e6cc 100%)',
  header: 'linear-gradient(90deg, #8b5e34 0%, #c4a15a 50%, #a67c3a 100%)',
  title: 'linear-gradient(90deg, #6b4423 0%, #a67c3a 45%, #c4a15a 100%)',
  preview: 'linear-gradient(135deg, #efe0c0 0%, #c4a15a 40%, #8b5e34 75%, #6b4423 100%)',
  primary: 'linear-gradient(90deg, #8b5e34, #c4a15a)',
  secondary: 'linear-gradient(90deg, #d4a574, #b8860b)',
  accent: 'linear-gradient(90deg, #a1887f, #8b5e34)',
}

const JADE_GRADIENTS = {
  page: 'linear-gradient(160deg, #e8f7f0 0%, #d5f0e4 35%, #c5ebe0 70%, #e6f6ef 100%)',
  surface: 'linear-gradient(145deg, #f5fcf8 0%, #e8f7f0 50%, #dff3ea 100%)',
  surfaceVariant: 'linear-gradient(90deg, #bbf7d0 0%, #a7f3d0 50%, #fde68a 100%)',
  editor: 'linear-gradient(180deg, #f7fdf9 0%, #eaf8f1 50%, #e3f5ec 100%)',
  header: 'linear-gradient(90deg, #047857 0%, #10b981 50%, #d4af37 100%)',
  title: 'linear-gradient(90deg, #065f46 0%, #059669 45%, #b8860b 100%)',
  preview: 'linear-gradient(135deg, #d5f0e4 0%, #10b981 40%, #047857 70%, #d4af37 100%)',
  primary: 'linear-gradient(90deg, #059669, #10b981, #d4af37)',
  secondary: 'linear-gradient(90deg, #d4af37, #f0d78c)',
  accent: 'linear-gradient(90deg, #34d399, #059669)',
}

const CRIMSON_GRADIENTS = {
  page: 'linear-gradient(165deg, #1a0a10 0%, #2c0f18 30%, #3a121f 55%, #16080e 100%)',
  surface: 'linear-gradient(145deg, #2a1018 0%, #3a1420 50%, #1c0c12 100%)',
  surfaceVariant: 'linear-gradient(90deg, #9f1239 0%, #881337 50%, #4c0519 100%)',
  editor: 'linear-gradient(180deg, #1e0c12 0%, #2a1018 45%, #180a10 100%)',
  header: 'linear-gradient(90deg, #7f1d1d 0%, #be185d 50%, #9f1239 100%)',
  title: 'linear-gradient(90deg, #fda4af 0%, #fb7185 45%, #e11d48 100%)',
  preview: 'linear-gradient(135deg, #1e0c12 0%, #9f1239 40%, #be185d 70%, #fda4af 100%)',
  primary: 'linear-gradient(90deg, #e11d48, #be185d, #fda4af)',
  secondary: 'linear-gradient(90deg, #f9a8d4, #fb7185)',
  accent: 'linear-gradient(90deg, #9f1239, #be185d)',
}

const GLACIER_GRADIENTS = {
  page: 'linear-gradient(160deg, #eef6fb 0%, #e3f0f8 35%, #d5eaf5 70%, #eef7fc 100%)',
  surface: 'linear-gradient(145deg, #f8fcff 0%, #eef6fb 50%, #e6f2f9 100%)',
  surfaceVariant: 'linear-gradient(90deg, #bae6fd 0%, #e0f2fe 50%, #e2e8f0 100%)',
  editor: 'linear-gradient(180deg, #f9fcff 0%, #eef7fc 50%, #e8f3fa 100%)',
  header: 'linear-gradient(90deg, #0284c7 0%, #38bdf8 50%, #94a3b8 100%)',
  title: 'linear-gradient(90deg, #0c4a6e 0%, #0284c7 45%, #64748b 100%)',
  preview: 'linear-gradient(135deg, #e3f0f8 0%, #7dd3fc 40%, #0284c7 75%, #64748b 100%)',
  primary: 'linear-gradient(90deg, #0284c7, #38bdf8, #94a3b8)',
  secondary: 'linear-gradient(90deg, #bae6fd, #7dd3fc)',
  accent: 'linear-gradient(90deg, #64748b, #0284c7)',
}

const DUSK_GRADIENTS = {
  page: 'linear-gradient(165deg, #1a1028 0%, #2d1638 28%, #4a1d3a 52%, #1e1230 78%, #120c20 100%)',
  surface: 'linear-gradient(145deg, #241434 0%, #3a1c38 50%, #1c1028 100%)',
  surfaceVariant: 'linear-gradient(90deg, #9f1239 0%, #6b21a8 50%, #075985 100%)',
  editor: 'linear-gradient(180deg, #1a1028 0%, #281638 42%, #160e24 100%)',
  header: 'linear-gradient(90deg, #fb7185 0%, #c084fc 45%, #38bdf8 100%)',
  title: 'linear-gradient(90deg, #fda4af 0%, #d8b4fe 45%, #7dd3fc 100%)',
  preview: 'linear-gradient(135deg, #1a1028 0%, #fb7185 35%, #c084fc 65%, #38bdf8 100%)',
  primary: 'linear-gradient(90deg, #fb7185, #c084fc, #38bdf8)',
  secondary: 'linear-gradient(90deg, #fda4af, #f9a8d4)',
  accent: 'linear-gradient(90deg, #c084fc, #38bdf8)',
}

const buildEffectCssVars = (gradients: {
  page: string
  surface: string
  surfaceVariant: string
  editor: string
  header: string
  title: string
}): ThemeEffectCssVars => ({
  '--escribonauta-fx-page': gradients.page,
  '--escribonauta-fx-surface': gradients.surface,
  '--escribonauta-fx-surface-variant': gradients.surfaceVariant,
  '--escribonauta-fx-editor': gradients.editor,
  '--escribonauta-fx-header': gradients.header,
  '--escribonauta-fx-title': gradients.title,
})

export const THEME_DEFINITIONS: Record<Theme, ThemeDefinition> = {
  light: {
    label: 'Claro',
    dark: false,
    colors: {
      primary: '#1D3557',
      secondary: '#FF9F1C',
      accent: '#9A8CFF',
      background: '#F7F9FB',
      error: '#E63946',
      success: '#2A9D8F',
      warning: '#E9C46A',
      info: '#457B9D',
      surface: '#F7F9FB',
      'surface-variant': '#ECEFF4',
      'editor-background': '#F7F9FB',
    },
  },
  dark: {
    label: 'Oscuro',
    dark: true,
    colors: {
      primary: '#A8C7FF',
      secondary: '#FFD280',
      accent: '#C4BFFF',
      background: '#121212',
      error: '#FF6B6B',
      success: '#34D399',
      warning: '#FBBF24',
      info: '#8AB4F8',
      surface: '#1E1E1E',
      'surface-variant': '#2A2A2A',
      'editor-background': '#1E1E1E',
    },
  },
  rose: {
    label: 'Rosado',
    dark: false,
    colors: {
      primary: '#C2185B',
      secondary: '#FF9F1C',
      accent: '#F48FB1',
      background: '#FFF5F8',
      error: '#E63946',
      success: '#2A9D8F',
      warning: '#E9C46A',
      info: '#AD1457',
      surface: '#FFFFFF',
      'surface-variant': '#FFE4EC',
      'editor-background': '#FFF0F5',
    },
  },
  sepia: {
    label: 'Sepia',
    dark: false,
    colors: {
      primary: '#5D4037',
      secondary: '#D4A574',
      accent: '#A1887F',
      background: '#F4ECD8',
      error: '#C62828',
      success: '#558B2F',
      warning: '#F9A825',
      info: '#6D4C41',
      surface: '#FAF3E8',
      'surface-variant': '#EDE0CE',
      'editor-background': '#FAF3E8',
    },
  },
  ocean: {
    label: 'Océano',
    dark: false,
    colors: {
      primary: '#00695C',
      secondary: '#FF9F1C',
      accent: '#4DD0E1',
      background: '#E8F4F8',
      error: '#E63946',
      success: '#2A9D8F',
      warning: '#E9C46A',
      info: '#0277BD',
      surface: '#FFFFFF',
      'surface-variant': '#D4EDF5',
      'editor-background': '#F0FAFC',
    },
  },
  midnight: {
    label: 'Medianoche',
    dark: true,
    colors: {
      primary: '#58A6FF',
      secondary: '#FFD280',
      accent: '#A371F7',
      background: '#0D1117',
      error: '#FF6B6B',
      success: '#3FB950',
      warning: '#D29922',
      info: '#79C0FF',
      surface: '#161B22',
      'surface-variant': '#21262D',
      'editor-background': '#161B22',
    },
  },
  green: {
    label: 'Verde',
    dark: false,
    colors: {
      primary: '#2E7D32',
      secondary: '#66BB6A',
      accent: '#81C784',
      background: '#F1F8E9',
      error: '#C62828',
      success: '#388E3C',
      warning: '#F9A825',
      info: '#00897B',
      surface: '#FFFFFF',
      'surface-variant': '#DCEDC8',
      'editor-background': '#F5FBF0',
    },
  },
  purple: {
    label: 'Morado',
    dark: false,
    colors: {
      primary: '#4A148C',
      secondary: '#9C27B0',
      accent: '#7B1FA2',
      background: '#E8DAEF',
      error: '#E63946',
      success: '#2A9D8F',
      warning: '#E9C46A',
      info: '#4527A0',
      surface: '#F3E5F5',
      'surface-variant': '#D1C4E9',
      'editor-background': '#EDE0F5',
    },
  },
  yellow: {
    label: 'Amarillo',
    dark: false,
    colors: {
      primary: '#F57F17',
      secondary: '#FFD54F',
      accent: '#FFCA28',
      background: '#FFFDE7',
      error: '#D32F2F',
      success: '#689F38',
      warning: '#FF8F00',
      info: '#F9A825',
      surface: '#FFFFFF',
      'surface-variant': '#FFF9C4',
      'editor-background': '#FFFEF5',
    },
  },
  rainbow: {
    label: 'Arcoíris',
    dark: false,
    previewBackground: RAINBOW_GRADIENTS.preview,
    swatchGradients: {
      primary: RAINBOW_GRADIENTS.primary,
      secondary: RAINBOW_GRADIENTS.secondary,
      accent: RAINBOW_GRADIENTS.accent,
      background: RAINBOW_GRADIENTS.page,
      'editor-background': RAINBOW_GRADIENTS.editor,
    },
    colors: {
      primary: '#E40303',
      secondary: '#FF8C00',
      accent: '#732982',
      background: '#FFFEF8',
      error: '#E40303',
      success: '#008026',
      warning: '#FFED00',
      info: '#24408E',
      surface: '#FFFFFF',
      'surface-variant': '#F5F0FF',
      'editor-background': '#FFFEF8',
    },
  },
  aurora: {
    label: 'Aurora',
    dark: true,
    isPremium: true,
    previewBackground: AURORA_GRADIENTS.preview,
    swatchGradients: {
      primary: AURORA_GRADIENTS.primary,
      secondary: AURORA_GRADIENTS.secondary,
      accent: AURORA_GRADIENTS.accent,
      background: AURORA_GRADIENTS.page,
      'editor-background': AURORA_GRADIENTS.editor,
      surface: AURORA_GRADIENTS.surface,
    },
    effectCssVars: buildEffectCssVars(AURORA_GRADIENTS),
    colors: {
      primary: '#5EEAD4',
      secondary: '#FBBF24',
      accent: '#A78BFA',
      background: '#0B1A22',
      error: '#FB7185',
      success: '#34D399',
      warning: '#FBBF24',
      info: '#38BDF8',
      surface: '#122830',
      'surface-variant': '#1A3640',
      'editor-background': '#0F222A',
    },
  },
  embers: {
    label: 'Brasas',
    dark: true,
    isPremium: true,
    previewBackground: EMBERS_GRADIENTS.preview,
    swatchGradients: {
      primary: EMBERS_GRADIENTS.primary,
      secondary: EMBERS_GRADIENTS.secondary,
      accent: EMBERS_GRADIENTS.accent,
      background: EMBERS_GRADIENTS.page,
      'editor-background': EMBERS_GRADIENTS.editor,
      surface: EMBERS_GRADIENTS.surface,
    },
    effectCssVars: buildEffectCssVars(EMBERS_GRADIENTS),
    colors: {
      primary: '#FB923C',
      secondary: '#FBBF24',
      accent: '#FB7185',
      background: '#1A0C0A',
      error: '#EF4444',
      success: '#84CC16',
      warning: '#F59E0B',
      info: '#F97316',
      surface: '#271410',
      'surface-variant': '#3B1C14',
      'editor-background': '#1C0F0C',
    },
  },
  nebula: {
    label: 'Nébula',
    dark: true,
    isPremium: true,
    previewBackground: NEBULA_GRADIENTS.preview,
    swatchGradients: {
      primary: NEBULA_GRADIENTS.primary,
      secondary: NEBULA_GRADIENTS.secondary,
      accent: NEBULA_GRADIENTS.accent,
      background: NEBULA_GRADIENTS.page,
      'editor-background': NEBULA_GRADIENTS.editor,
      surface: NEBULA_GRADIENTS.surface,
    },
    effectCssVars: buildEffectCssVars(NEBULA_GRADIENTS),
    colors: {
      primary: '#C4B5FD',
      secondary: '#F9A8D4',
      accent: '#38BDF8',
      background: '#100824',
      error: '#FB7185',
      success: '#34D399',
      warning: '#FBBF24',
      info: '#818CF8',
      surface: '#1C0F3C',
      'surface-variant': '#2A1658',
      'editor-background': '#140A2E',
    },
  },
  parchment: {
    label: 'Pergamino',
    dark: false,
    isPremium: true,
    previewBackground: PARCHMENT_GRADIENTS.preview,
    swatchGradients: {
      primary: PARCHMENT_GRADIENTS.primary,
      secondary: PARCHMENT_GRADIENTS.secondary,
      accent: PARCHMENT_GRADIENTS.accent,
      background: PARCHMENT_GRADIENTS.page,
      'editor-background': PARCHMENT_GRADIENTS.editor,
      surface: PARCHMENT_GRADIENTS.surface,
    },
    effectCssVars: buildEffectCssVars(PARCHMENT_GRADIENTS),
    colors: {
      primary: '#8B5E34',
      secondary: '#C4A15A',
      accent: '#A1887F',
      background: '#F3E6C8',
      error: '#B91C1C',
      success: '#4D7C0F',
      warning: '#CA8A04',
      info: '#92400E',
      surface: '#FFF8EA',
      'surface-variant': '#E8D4AD',
      'editor-background': '#FFFAF0',
    },
  },
  jade: {
    label: 'Jade',
    dark: false,
    isPremium: true,
    previewBackground: JADE_GRADIENTS.preview,
    swatchGradients: {
      primary: JADE_GRADIENTS.primary,
      secondary: JADE_GRADIENTS.secondary,
      accent: JADE_GRADIENTS.accent,
      background: JADE_GRADIENTS.page,
      'editor-background': JADE_GRADIENTS.editor,
      surface: JADE_GRADIENTS.surface,
    },
    effectCssVars: buildEffectCssVars(JADE_GRADIENTS),
    colors: {
      primary: '#059669',
      secondary: '#D4AF37',
      accent: '#34D399',
      background: '#E6F6EF',
      error: '#DC2626',
      success: '#16A34A',
      warning: '#CA8A04',
      info: '#0D9488',
      surface: '#F5FCF8',
      'surface-variant': '#D5F0E4',
      'editor-background': '#F7FDF9',
    },
  },
  crimson: {
    label: 'Carmesí',
    dark: true,
    isPremium: true,
    previewBackground: CRIMSON_GRADIENTS.preview,
    swatchGradients: {
      primary: CRIMSON_GRADIENTS.primary,
      secondary: CRIMSON_GRADIENTS.secondary,
      accent: CRIMSON_GRADIENTS.accent,
      background: CRIMSON_GRADIENTS.page,
      'editor-background': CRIMSON_GRADIENTS.editor,
      surface: CRIMSON_GRADIENTS.surface,
    },
    effectCssVars: buildEffectCssVars(CRIMSON_GRADIENTS),
    colors: {
      primary: '#FB7185',
      secondary: '#F9A8D4',
      accent: '#FDA4AF',
      background: '#16080E',
      error: '#EF4444',
      success: '#4ADE80',
      warning: '#FBBF24',
      info: '#F472B6',
      surface: '#2A1018',
      'surface-variant': '#3A121F',
      'editor-background': '#1E0C12',
    },
  },
  glacier: {
    label: 'Glaciar',
    dark: false,
    isPremium: true,
    previewBackground: GLACIER_GRADIENTS.preview,
    swatchGradients: {
      primary: GLACIER_GRADIENTS.primary,
      secondary: GLACIER_GRADIENTS.secondary,
      accent: GLACIER_GRADIENTS.accent,
      background: GLACIER_GRADIENTS.page,
      'editor-background': GLACIER_GRADIENTS.editor,
      surface: GLACIER_GRADIENTS.surface,
    },
    effectCssVars: buildEffectCssVars(GLACIER_GRADIENTS),
    colors: {
      primary: '#0284C7',
      secondary: '#94A3B8',
      accent: '#7DD3FC',
      background: '#EEF6FB',
      error: '#DC2626',
      success: '#0D9488',
      warning: '#CA8A04',
      info: '#0EA5E9',
      surface: '#F8FCFF',
      'surface-variant': '#D5EAF5',
      'editor-background': '#F9FCFF',
    },
  },
  dusk: {
    label: 'Ocaso',
    dark: true,
    isPremium: true,
    previewBackground: DUSK_GRADIENTS.preview,
    swatchGradients: {
      primary: DUSK_GRADIENTS.primary,
      secondary: DUSK_GRADIENTS.secondary,
      accent: DUSK_GRADIENTS.accent,
      background: DUSK_GRADIENTS.page,
      'editor-background': DUSK_GRADIENTS.editor,
      surface: DUSK_GRADIENTS.surface,
    },
    effectCssVars: buildEffectCssVars(DUSK_GRADIENTS),
    colors: {
      primary: '#FDA4AF',
      secondary: '#D8B4FE',
      accent: '#7DD3FC',
      background: '#120C20',
      error: '#FB7185',
      success: '#34D399',
      warning: '#FBBF24',
      info: '#C084FC',
      surface: '#241434',
      'surface-variant': '#3A1C38',
      'editor-background': '#1A1028',
    },
  },
}

export const THEME_OPTIONS = (Object.entries(THEME_DEFINITIONS) as [Theme, ThemeDefinition][]).map(
  ([value, { label }]) => ({ label, value }),
)

export const THEME_PREVIEW_SWATCHES = [
  'primary',
  'secondary',
  'accent',
  'background',
  'editor-background',
] as const satisfies (keyof ThemePalette)[]

export const THEME_CARDS = (Object.entries(THEME_DEFINITIONS) as [Theme, ThemeDefinition][]).map(
  ([id, definition]) => ({
    id,
    label: definition.label,
    colors: definition.colors,
    isPremium: definition.isPremium === true,
    previewBackground: definition.previewBackground,
    swatchGradients: definition.swatchGradients,
  }),
)

export const DARK_THEMES: Theme[] = (Object.entries(THEME_DEFINITIONS) as [Theme, ThemeDefinition][])
  .filter(([, def]) => def.dark)
  .map(([id]) => id)

export const PREMIUM_THEMES: Theme[] = (Object.entries(THEME_DEFINITIONS) as [Theme, ThemeDefinition][])
  .filter(([, def]) => def.isPremium === true)
  .map(([id]) => id)

export const isDarkTheme = (theme: Theme): boolean => DARK_THEMES.includes(theme)

export const isPremiumTheme = (theme: Theme): boolean => PREMIUM_THEMES.includes(theme)

export const EFFECT_THEME_CSS_VAR_KEYS = [
  '--escribonauta-fx-page',
  '--escribonauta-fx-surface',
  '--escribonauta-fx-surface-variant',
  '--escribonauta-fx-editor',
  '--escribonauta-fx-header',
  '--escribonauta-fx-title',
] as const

export const THEME_TO_VUETIFY: Record<Theme, string> = {
  light: 'lightTheme',
  dark: 'darkTheme',
  rose: 'roseTheme',
  sepia: 'sepiaTheme',
  ocean: 'oceanTheme',
  midnight: 'midnightTheme',
  green: 'greenTheme',
  purple: 'purpleTheme',
  yellow: 'yellowTheme',
  rainbow: 'rainbowTheme',
  aurora: 'auroraTheme',
  embers: 'embersTheme',
  nebula: 'nebulaTheme',
  parchment: 'parchmentTheme',
  jade: 'jadeTheme',
  crimson: 'crimsonTheme',
  glacier: 'glacierTheme',
  dusk: 'duskTheme',
}
